function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}

(() => {
  const reviewAnchorId = 'heitok-customer-reviews';
  const reviewHref = `/#${reviewAnchorId}`;
  const amazonHref = 'https://www.amazon.com/dp/B0CRHPHV79';

  const normalizeText = (text) => text.replace(/\s+/g, ' ').trim().toLowerCase();

  const ensureReviewAnchor = () => {
    if (document.getElementById(reviewAnchorId)) return;

    const reviewsSection = document
      .querySelector('.r--featured-section, ryviu-feature-extend')
      ?.closest('.shopify-section');

    if (!reviewsSection || !reviewsSection.parentNode) return;

    const anchor = document.createElement('span');
    anchor.id = reviewAnchorId;
    anchor.setAttribute('aria-hidden', 'true');
    reviewsSection.parentNode.insertBefore(anchor, reviewsSection);
  };

  const patchFooterLinks = () => {
    const footer = document.querySelector('footer.footer');
    if (!footer) return;

    ensureReviewAnchor();

    const footerLinks = Array.from(footer.querySelectorAll('a'));
    const reviewLink = footerLinks.find((link) => {
      const href = link.getAttribute('href') || '';
      const label = normalizeText(link.textContent || '');
      return href === 'https://t.ly/jER4V' || label === 'customer reviews';
    });

    if (!reviewLink) return;

    reviewLink.setAttribute('href', reviewHref);
    reviewLink.removeAttribute('target');
    reviewLink.removeAttribute('rel');

    const list = reviewLink.closest('ul');
    if (!list) return;

    const hasAmazonLink = Array.from(list.querySelectorAll('a')).some((link) => link.href === amazonHref);
    if (hasAmazonLink) return;

    const item = document.createElement('li');
    const link = document.createElement('a');
    link.href = amazonHref;
    link.className = 'link link--text list-menu__item list-menu__item--link';
    link.textContent = 'Shop on Amazon';
    item.appendChild(link);
    list.appendChild(item);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', patchFooterLinks);
  } else {
    patchFooterLinks();
  }

  document.addEventListener('shopify:section:load', patchFooterLinks);
})();

(() => {
  const rootId = 'am-id-5137';
  const fallbackOrderAttr = 'data-heitok-review-order';
  const nativeReviewHideClass = 'heitok-hide-native-amazon-reviews';

  const hideNativeAmazonReviewsWhenFeatured = () => {
    if (!document.querySelector('.heitok-featured-reviews')) return false;

    const root = document.getElementById(rootId);
    if (!root) return true;

    const appBlock = root.closest('.shopify-app-block');
    (appBlock || root).classList.add(nativeReviewHideClass);
    return true;
  };

  const isDefaultReviewImage = (value = '') => {
    const source = value.toLowerCase();
    return (
      source.includes('amazon-avatars-global/default') ||
      source.includes('kaktusapp.com/storage') ||
      source.includes('/storage/rvzrvi') ||
      source.includes('default._sx')
    );
  };

  const getStarCount = (item) =>
    item.querySelectorAll('.k-popup__review--rating-icon-filled, .k-popup__review--item__rating-icon-filled').length;

  const hasCustomerMedia = (item) => {
    const mediaTextPattern = /\b(video|play|media|thumbnail|preview)\b|\b\d{1,2}:\d{2}\b/i;

    if (item.querySelector('video, source, [poster]')) return true;

    const images = Array.from(item.querySelectorAll('img'));
    const hasImage = images.some((image) => {
      if (image.closest('.k-popup__review--item__author-avatar, .k-popup__review--item__source')) return false;

      const source = image.currentSrc || image.src || '';
      if (!source || isDefaultReviewImage(source)) return false;

      const width = image.naturalWidth || image.width || image.getBoundingClientRect().width;
      const height = image.naturalHeight || image.height || image.getBoundingClientRect().height;
      return width >= 80 && height >= 80;
    });

    if (hasImage) return true;

    return Array.from(item.querySelectorAll('*')).some((node) => {
      if (node.closest('.k-popup__review--item__author-avatar, .k-popup__review--item__source')) return false;

      const mediaLabel = [
        node.className,
        node.getAttribute('aria-label'),
        node.getAttribute('title'),
        node.getAttribute('href'),
        node.textContent,
      ]
        .filter(Boolean)
        .join(' ');

      if (mediaTextPattern.test(mediaLabel)) return true;

      const background = window.getComputedStyle(node).backgroundImage;
      return background && background !== 'none' && background.includes('url(') && !isDefaultReviewImage(background);
    });
  };

  const sortAmazonReviews = () => {
    if (hideNativeAmazonReviewsWhenFeatured()) return;

    const root = document.getElementById(rootId);
    const layout = root?.querySelector('.k-popup__review--layout');
    if (!layout) return;

    const items = Array.from(layout.querySelectorAll(':scope > .k-popup__review--item'));
    if (items.length < 2) return;

    items.forEach((item, index) => {
      if (!item.hasAttribute(fallbackOrderAttr)) item.setAttribute(fallbackOrderAttr, String(index));
    });

    const rankedItems = items.map((item) => {
      const starCount = getStarCount(item);
      const mediaReview = hasCustomerMedia(item);
      item.classList.toggle('heitok-review-has-media', mediaReview);
      item.classList.toggle('heitok-review-has-photo', mediaReview);
      const isFiveStar = starCount >= 5;

      return {
        item,
        originalIndex: Number(item.getAttribute(fallbackOrderAttr) || 0),
        score: (mediaReview && isFiveStar ? 1000 : 0) + (isFiveStar ? 100 : 0) + (mediaReview ? 10 : 0) + starCount,
      };
    });

    rankedItems.sort((a, b) => b.score - a.score || a.originalIndex - b.originalIndex);

    const changed = rankedItems.some(({ item }, index) => item !== items[index]);
    if (!changed) return;

    const fragment = document.createDocumentFragment();
    rankedItems.forEach(({ item }) => fragment.appendChild(item));
    layout.appendChild(fragment);
  };

  let reviewSortTimer;
  let reviewObserver;

  const scheduleAmazonReviewSort = () => {
    window.clearTimeout(reviewSortTimer);
    reviewSortTimer = window.setTimeout(sortAmazonReviews, 120);
  };

  const startAmazonReviewSort = () => {
    if (hideNativeAmazonReviewsWhenFeatured()) return;

    const root = document.getElementById(rootId);
    if (!root) return;

    scheduleAmazonReviewSort();
    if (reviewObserver) return;

    reviewObserver = new MutationObserver(scheduleAmazonReviewSort);
    reviewObserver.observe(root, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startAmazonReviewSort);
  } else {
    startAmazonReviewSort();
  }

  window.setTimeout(startAmazonReviewSort, 700);
  window.setTimeout(startAmazonReviewSort, 1800);
  window.setTimeout(startAmazonReviewSort, 3600);
  document.addEventListener('shopify:section:load', startAmazonReviewSort);
})();

(() => {
  const series = [
    ['Clay Poker Set', '14g clay', 'Heavier clay composite chips for a more authentic casino-style table feel.', '/collections/clay-poker-chips-set'],
    ['Touched Poker Set', 'Modern', 'Clean stripe styling with balanced 11.5g chips and a polished modern layout.', '/collections/touch-poker-chips-set'],
    ['Classic Poker Set', 'Classic', 'Traditional dice-style chips for quick reads, simple setup, and easy table use.', '/collections/classic-poker-set'],
    ['Sparkle Poker Set', 'Gift look', 'Diamond-style chips with a polished visual effect for a more dressed-up table.', '/collections/sparkle-poker-set'],
    ['Kaleido Poker Set', 'Color', 'Colorful prism-style chips for lively game nights and easier stack recognition.', '/collections/kaleido-poker-set'],
  ];

  const ensureCacheFallbackStyles = () => {
    if (document.getElementById('heitok-cache-fallback-style')) return;

    const style = document.createElement('style');
    style.id = 'heitok-cache-fallback-style';
    style.textContent = `
      .heitok-cache-section{padding:32px 0;background:#fff}
      .heitok-cache-wrap{max-width:1200px;margin:0 auto;padding:0 1.5rem}
      .heitok-cache-eyebrow{margin:0 0 .8rem;color:rgba(8,9,10,.58);font-size:1.1rem;font-weight:760;text-transform:uppercase}
      .heitok-cache-heading{margin:0;color:#090909;font-size:clamp(2.2rem,4vw,4rem);line-height:1.08}
      .heitok-cache-text{max-width:68rem;margin:1rem 0 0;color:rgba(8,9,10,.68);font-size:1.45rem;line-height:1.65}
      .heitok-cache-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:1.2rem;margin-top:1.8rem}
      .heitok-cache-card{border:1px solid rgba(202,160,82,.2);border-radius:.8rem;background:#fff;box-shadow:0 1rem 2.8rem rgba(5,8,12,.05);padding:1.5rem}
      .heitok-cache-label{display:inline-flex;border:1px solid rgba(202,160,82,.54);border-radius:999px;background:linear-gradient(135deg,#050505,#17110a);color:#f4e4b1;font-size:1rem;font-weight:760;padding:.6rem .85rem;text-transform:uppercase}
      .heitok-cache-card h3{margin:1.2rem 0 .7rem;color:#090909;font-size:1.7rem;line-height:1.18}
      .heitok-cache-card p{margin:0;color:rgba(8,9,10,.68);font-size:1.28rem;line-height:1.55}
      .heitok-cache-link{display:inline-flex;margin-top:1.2rem;border-bottom:1px solid currentColor;color:#7a5a18;font-size:1.1rem;font-weight:760;text-decoration:none;text-transform:uppercase}
      .r--featured-section,.r--widget-section,.r--questions-section,.r--star-section,ryviu-feature-extend,.product-detail-gallery{display:none!important}
      .ryviu-collection{display:none!important}
      @media(max-width:989px){.heitok-cache-grid{grid-template-columns:repeat(2,minmax(0,1fr))}}
      @media(max-width:749px){.heitok-cache-section{padding:26px 0}.heitok-cache-grid{grid-template-columns:1fr;gap:1rem}.heitok-cache-card{padding:1.35rem}}
    `;
    document.head.appendChild(style);
  };

  const hideLegacyReviewApps = () => {
    if (document.querySelector('.heitok-featured-reviews')) {
      document.querySelectorAll('.heitok-app-reviews, #heitok-cache-reviews').forEach((node) => {
        const section = node.closest('.shopify-section') || node;
        section.style.display = 'none';
      });
    }

    document.querySelectorAll('.r--featured-section, .r--widget-section, .r--questions-section, .r--star-section').forEach((node) => {
      const section = node.closest('.shopify-section') || node;
      section.style.display = 'none';
    });

    document.querySelectorAll('.product-detail-gallery').forEach((node) => {
      const section = node.closest('.shopify-section') || node;
      section.style.display = 'none';
    });
  };

  const normalizeAllProductsLinks = () => {
    document.querySelectorAll('a[href]').forEach((anchor) => {
      const rawHref = anchor.getAttribute('href');
      if (!rawHref || !rawHref.includes('/collections/all')) return;

      try {
        const url = new URL(rawHref, window.location.origin);
        if (url.pathname !== '/collections/all') return;
        if (url.searchParams.get('view') === 'codex2') return;

        url.searchParams.set('view', 'codex2');
        const normalizedHref =
          url.origin === window.location.origin ? `${url.pathname}${url.search}${url.hash}` : url.toString();
        anchor.setAttribute('href', normalizedHref);
      } catch (error) {
        // Ignore malformed links and leave Shopify-generated markup untouched.
      }
    });
  };

  const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const replaceTextTokens = (value, tokens = {}) =>
    Object.entries(tokens).reduce(
      (text, [token, replacement]) => text.replace(new RegExp(escapeRegExp(`{{ ${token} }}`), 'g'), replacement),
      value
    );

  const escapeHtml = (value) => {
    const entities = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
    return String(value).replace(/[&<>"']/g, (character) => entities[character]);
  };

  const getProductCount = (signature) => {
    const countMatch = signature.match(/\b(1000|600|500|300|200|100)[-\s]?(?:piece|pcs|pc|chips?)?\b/);
    return countMatch ? countMatch[1] : '300';
  };

  const getProductProfile = (signature) => {
    if (signature.includes('texas-holdem-poker-mat') || signature.includes('poker mat') || signature.includes('table mat')) {
      return {
        eyebrow: 'HEITOK Poker Table Mat',
        title: "Texas Hold'em Poker Mat 94 x 47 in",
        summary:
          "A large felt-style poker mat that turns a dining table or game table into a cleaner Texas Hold'em, blackjack, and casino-night setup.",
      };
    }

    const count = getProductCount(signature);
    const profileMap = [
      {
        test: /clay|14g/,
        eyebrow: 'Clay Poker Set',
        title: '{{ count }}-Piece 14g Clay Poker Set',
        summary:
          'A premium {{ count }}-piece 14g clay poker set with heavier hand feel, crisp stack sound, and a stronger casino-style presence for serious home tables.',
      },
      {
        test: /touch|touched/,
        eyebrow: 'Touched Poker Set',
        title: '{{ count }}-Piece 11.5g Touched Poker Set',
        summary:
          "A modern {{ count }}-piece poker set with 11.5g weighted chips, long-stripe styling, and a cleaner look for organized cash games and home tables.",
      },
      {
        test: /sparkle|aura|diamond/,
        eyebrow: 'Sparkle Poker Set',
        title: '{{ count }}-Piece 11.5g Sparkle Poker Set',
        summary:
          'A {{ count }}-piece diamond-style poker set with 11.5g weighted chips and a gift-ready table look for parties, blackjack, and dressed-up game nights.',
      },
      {
        test: /kaleido|prism/,
        eyebrow: 'Kaleido Poker Set',
        title: '{{ count }}-Piece 11.5g Kaleido Poker Set',
        summary:
          'A {{ count }}-piece prism-style poker set with 11.5g weighted chips for lively game nights, fast stack recognition, blackjack, and easy table organization.',
      },
      {
        test: /classic|dice/,
        eyebrow: 'Classic Poker Set',
        title: '{{ count }}-Piece 11.5g Classic Poker Set',
        summary:
          'A familiar {{ count }}-piece dice-style poker set with 11.5g weighted chips for first-time hosts, family game nights, blackjack, and easy home setup.',
      },
    ];

    const profile = profileMap.find(({ test }) => test.test(signature)) || profileMap[4];
    return {
      eyebrow: profile.eyebrow,
      title: replaceTextTokens(profile.title, { count }),
      summary: replaceTextTokens(profile.summary, { count }),
    };
  };

  const normalizeProductCopy = () => {
    if (!window.location.pathname.includes('/products/')) return;

    const titleNode = document.querySelector('.product__title h1, h1');
    if (!titleNode) return;

    const summaryNode = document.querySelector('.heitok-product-summary');
    const eyebrowNode = document.querySelector('.heitok-product-eyebrow');
    const visibleText = `${titleNode.textContent || ''} ${summaryNode?.textContent || ''}`;
    const signature = `${visibleText} ${document.title} ${window.location.pathname}`.toLowerCase();
    const isStaleCopy =
      signature.includes('poker chips set') ||
      signature.includes('professional casino blackjack set') ||
      signature.includes('high-end carrying case') ||
      signature.includes('texas-holdem-poker-mat-94-x-47');

    if (!isStaleCopy) return;

    const profile = getProductProfile(signature);
    if (eyebrowNode) eyebrowNode.textContent = profile.eyebrow;
    titleNode.textContent = profile.title;
    if (summaryNode) summaryNode.textContent = profile.summary;
  };

  const buildSeriesGuide = () => {
    const cards = series
      .map(
        ([title, label, text, href]) => `
          <a class="heitok-cache-card" href="${href}">
            <span class="heitok-cache-label">${label}</span>
            <h3>${title}</h3>
            <p>${text}</p>
            <span class="heitok-cache-link">Explore series</span>
          </a>
        `
      )
      .join('');

    return `
      <section class="heitok-cache-section" id="heitok-cache-series-guide">
        <div class="heitok-cache-wrap">
          <p class="heitok-cache-eyebrow">Choose your poker set</p>
          <h2 class="heitok-cache-heading">Choose Your Poker Set</h2>
          <p class="heitok-cache-text">Compare HEITOK Clay, Touched, Classic, Sparkle, and Kaleido poker chip sets by weight, table feel, visual style, and game-night use.</p>
          <div class="heitok-cache-grid">${cards}</div>
        </div>
      </section>
    `;
  };

  const insertHomeGuide = () => {
    if (document.querySelector('.heitok-series-guide, #heitok-cache-series-guide')) return;
    const hero = document.querySelector('[id$="__image-with-text"]');
    if (!hero) return;
    hero.insertAdjacentHTML('afterend', buildSeriesGuide());
  };

  const insertProductStory = () => {
    if (!window.location.pathname.includes('/products/') || document.querySelector('.heitok-product-story, #heitok-cache-product-story')) return;

    const main = document.querySelector('[id$="__main"]');
    if (!main) return;

    const title = document.querySelector('h1')?.textContent?.replace(/\s+/g, ' ').trim() || 'HEITOK Poker Set';
    const signature = `${title} ${window.location.pathname}`.toLowerCase();
    const profile = getProductProfile(signature);
    const isMat = profile.eyebrow === 'HEITOK Poker Table Mat';
    const isClay = profile.eyebrow === 'Clay Poker Set';
    const chipWeight = isClay ? '14g' : '11.5g';
    const safeSeriesName = escapeHtml(profile.eyebrow);
    const intro = isMat
      ? "A 94 x 47 in roll-out poker mat for Texas Hold'em, blackjack, and cleaner dining-table casino-night setup."
      : `${profile.title} is built for Texas Hold'em, blackjack, and game-night hosting with ${chipWeight} weighted chips, organized storage, and table-ready accessories.`;
    const firstCardTitle = isMat ? '94 x 47 in table surface' : `${chipWeight} weighted feel`;
    const firstCardText = isMat
      ? 'Creates a cleaner felt-style play area for cards, chips, betting action, and shared pots.'
      : 'Substantial chips for stacking, shuffling, and a more polished table presence.';
    const includedTitle = isMat ? 'Roll-out poker mat' : 'Set and accessories';
    const includedText = isMat
      ? 'The table mat stores compactly between games and turns a standard table into a dedicated play surface.'
      : 'Chips, case or tray, and the accessories shown with this product stay ready between games.';
    const specsText = isMat
      ? 'Large table coverage, felt-style surface, rollable storage, and quick home casino setup.'
      : 'Weighted composite poker chips, clear value options, organized storage, and game-night setup.';

    main.insertAdjacentHTML(
      'afterend',
      `
        <section class="heitok-cache-section" id="heitok-cache-product-story">
          <div class="heitok-cache-wrap">
            <p class="heitok-cache-eyebrow">${safeSeriesName}</p>
            <h2 class="heitok-cache-heading">A better poker set for home casino nights</h2>
            <p class="heitok-cache-text">${escapeHtml(intro)}</p>
            <div class="heitok-cache-grid">
              <article class="heitok-cache-card"><span class="heitok-cache-label">Highlights</span><h3>${escapeHtml(firstCardTitle)}</h3><p>${escapeHtml(firstCardText)}</p></article>
              <article class="heitok-cache-card"><span class="heitok-cache-label">Best for</span><h3>Home poker nights</h3><p>Made for Texas Hold'em, blackjack, parties, and organized casual tournaments.</p></article>
              <article class="heitok-cache-card"><span class="heitok-cache-label">Included</span><h3>${escapeHtml(includedTitle)}</h3><p>${escapeHtml(includedText)}</p></article>
              <article class="heitok-cache-card"><span class="heitok-cache-label">Specs</span><h3>Quick details</h3><p>${escapeHtml(specsText)}</p></article>
            </div>
          </div>
        </section>
      `
    );
  };

  const insertReviews = () => {
    if (window.location.pathname.includes('/products/')) return;
    if (document.querySelector('.heitok-featured-reviews, .heitok-reviews, #heitok-cache-reviews')) return;
    const target =
      document.querySelector('[id$="__product_grid_v2"]') ||
      document.querySelector('[id$="__product-grid"]') ||
      document.querySelector('#heitok-cache-product-story') ||
      document.querySelector('[id$="__main"]');
    if (!target) return;

    target.insertAdjacentHTML(
      'afterend',
      `
        <section class="heitok-cache-section" id="heitok-cache-reviews">
          <div class="heitok-cache-wrap">
            <p class="heitok-cache-eyebrow">Customer reviews</p>
            <h2 class="heitok-cache-heading">Loved by home poker players</h2>
            <p class="heitok-cache-text">Selected feedback from players using HEITOK poker chip sets for Texas Hold'em, blackjack, parties, and regular home games.</p>
            <div class="heitok-cache-grid">
              <article class="heitok-cache-card"><span class="heitok-cache-label">★★★★★</span><h3>Beautiful set</h3><p>Well-made chips with clear values and a professional table look.</p></article>
              <article class="heitok-cache-card"><span class="heitok-cache-label">★★★★★</span><h3>Nice chip</h3><p>Smooth feel, strong weight, and a premium texture for game night.</p></article>
              <article class="heitok-cache-card"><span class="heitok-cache-label">★★★★★</span><h3>Amazing quality</h3><p>The set feels solid, polished, and ready for regular home games.</p></article>
              <article class="heitok-cache-card"><span class="heitok-cache-label">★★★★★</span><h3>Just like casino</h3><p>Great chips for cards with a more realistic casino-style feel.</p></article>
            </div>
          </div>
        </section>
      `
    );
  };

  const applyCacheFallbacks = () => {
    ensureCacheFallbackStyles();
    normalizeAllProductsLinks();
    normalizeProductCopy();
    hideLegacyReviewApps();
    insertHomeGuide();
    insertProductStory();
    insertReviews();
  };

  let fallbackTimer;
  const scheduleCacheFallbacks = () => {
    window.clearTimeout(fallbackTimer);
    fallbackTimer = window.setTimeout(applyCacheFallbacks, 80);
  };

  const startFallbackObserver = () => {
    if (!document.body || !window.MutationObserver) return;

    const observer = new MutationObserver(scheduleCacheFallbacks);
    observer.observe(document.body, { childList: true, subtree: true });

    window.setTimeout(() => observer.disconnect(), 12000);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      applyCacheFallbacks();
      startFallbackObserver();
      window.setTimeout(applyCacheFallbacks, 700);
      window.setTimeout(applyCacheFallbacks, 1800);
      window.setTimeout(applyCacheFallbacks, 3600);
    });
  } else {
    applyCacheFallbacks();
    startFallbackObserver();
    window.setTimeout(applyCacheFallbacks, 700);
    window.setTimeout(applyCacheFallbacks, 1800);
    window.setTimeout(applyCacheFallbacks, 3600);
  }

  document.addEventListener('shopify:section:load', applyCacheFallbacks);
})();
